/**
 * Transmittals Shared Types
 * Platform-agnostic type definitions for transmittal hooks
 *
 * This file imports core types from the shared schema and defines
 * additional frontend-specific types for hooks and components.
 */

// ============================================================================
// Import Shared Types from Schema (Single Source of Truth)
// ============================================================================

import type {
  // API Request/Response Types
  SaveTransmittalsRequest,
  SaveTransmittalsResponse,
  GetTransmittalDataByIdRequest,
  GetTransmittalDataByIdResponse,
  GetFilesAttachedToTransmittalRequest,
  GetFilesAttachedToTransmittalResponse,
  GetTransmittalFileVersionsForSpaceRequest,
  GetTransmittalFileVersionsForSpaceResponse,
  IsTransmittal403Request,
  IsTransmittal403Response,
  GetFormTemplatesForProjectRequest,
  GetFormTemplatesForProjectResponse,
  // Component Types
  TransmittalSpace,
  TransmittalSpaceUser,
  TransmittalFileMetadata as SharedTransmittalFileMetadata,
  TransmittalDetails,
  TransmittalFormTemplate,
  FormQuestionField,
  FormTemplateSection,
  FormFieldAnswer,
} from "@ServerTypes/transmittals/transmittals.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

// Re-export shared types for convenience
export type {
  SaveTransmittalsRequest,
  SaveTransmittalsResponse,
  GetTransmittalDataByIdRequest,
  GetTransmittalDataByIdResponse,
  GetFilesAttachedToTransmittalRequest,
  GetFilesAttachedToTransmittalResponse,
  GetTransmittalFileVersionsForSpaceRequest,
  GetTransmittalFileVersionsForSpaceResponse,
  IsTransmittal403Request,
  IsTransmittal403Response,
  GetFormTemplatesForProjectRequest,
  GetFormTemplatesForProjectResponse,
  TransmittalSpace,
  TransmittalSpaceUser,
  TransmittalDetails,
  TransmittalFormTemplate,
  FormQuestionField,
  FormTemplateSection,
  FormFieldAnswer,
};

// ============================================================================
// Core Entity Types (Frontend-specific extensions)
// ============================================================================

export interface Space {
  _id: string;
  spaceName: string;
  description?: string;
  createdDate?: number;
  status?: string;
  id?: string;
  createdBy?: string;
  spaceId?: string;
  projectId?: string;
  createdByName?: string;
}

export interface OriginSpace {
  _id: string;
  spaceName: string;
}

export interface SelectedUser {
  id: string; // Composite key: `${userId}_${spaceId}`
  userId?: string;
  spaceId: string;
  spaceName: string;
  userName?: string;
  email?: string;
  organisation?: string;
  designation?: string;
}

/** Space with users - uses shared TransmittalSpace type */
export type SpaceWithUsers = TransmittalSpace;

// ============================================================================
// Reference Types
// ============================================================================

export interface TransmittalReference {
  /** A file-version `_id`; sent as `attachedResources[].fileVersionId` / `forwardedResources[].resourceId`. */
  fileVersion: ObjectIdString;
  name: string;
  versionNumber: number;
  size: number;
  dateCreated: number;
  resourceType: string;
  fileType: string;
  remarks: string;
  /** A transmittal-version `_id`; sent as `forwardedResources[].transmittalId`. */
  transmittalVersionId?: ObjectIdString;
}

export interface ReferenceDatasetItem {
  id: string;
  name: string;
  versionNumber: number;
  size: number;
  dateCreated: number;
  resourceType: string;
  fileType: string;
  remarks: string;
}

// ============================================================================
// API Payload Types (derived from shared schema)
// ============================================================================

/**
 * One `toSpaceIds`/`ccSpaceIds` entry. Derived from the request schema so `spaceId`/`userId` keep
 * the `objectIdString` brand all the way into `saveTransmittalsData`.
 */
export type SpaceUserPayload = SaveTransmittalsRequest["toSpaceIds"][number];

export interface AttachedResourcePayload {
  fileVersionId: ObjectIdString;
  remarks: string;
}

/** Transmittal save payload - matches SaveTransmittalsRequest from shared schema */
export type TransmittalPayload = SaveTransmittalsRequest;
export type ForwardedResourcePayload = SaveTransmittalsRequest["forwardedResources"][number];

// ============================================================================
// Response Types (derived from shared schema)
// ============================================================================

/** Transmittal data structure - matches GetTransmittalDataByIdResponse.data (shared schema) */
export type TransmittalData = TransmittalDetails;

/** File metadata from transmittal - uses shared type */
export type TransmittalFileMetadata = SharedTransmittalFileMetadata;

/** Metadata container for transmittal files - matches GetTransmittalDataByIdResponse.metadata */
export interface TransmittalMetadata {
  files?: TransmittalFileMetadata[] | undefined;
}

/** Attached file for display in UI */
export interface AttachedFile {
  id: string;
  name: string;
  // Every optional here is filled from a server metadata field that is itself optional, so the
  // value really can arrive as an explicit `undefined` — the declarations say so.
  fileType?: string | undefined;
  version?: number | undefined;
  size?: number | undefined;
  addedOn?: number | undefined;
  resourceType?: string | undefined;
  isFolder: number;
  toSpaceData?: TransmittalSpace[] | undefined;
  remarks?: string | undefined;
}

// ============================================================================
// File Version Types
// ============================================================================

export interface FileVersionData {
  resourceVersionId: string;
  fileVersionNumber: number;
  createdDate: number;
  indexAtProjectLevel: number;
  transmittalId: string;
}

export interface FileVersionCheckResult {
  isLatest: boolean;
  currentVersion: FileVersionData | null;
  latestVersion: FileVersionData | null;
}

// ============================================================================
// Permission Types
// ============================================================================

/** Transmittal access response - matches IsTransmittal403Response */
export type TransmittalAccessResponse = IsTransmittal403Response;

export interface ModuleAccess {
  ready: boolean;
  /**
   * One of the numeric seat constants from `@Helpers/constants` (PORTAL_FULL_SEAT = 1 /
   * PORTAL_NO_SEAT = 0), as supplied by `useUserModuleAccess`, or `null` before the slices load.
   */
  portalSeat: number | null;
}

export interface ProjectSettings {
  transmittal?: {
    isVisible?: boolean;
    add?: {
      enable?: boolean;
    };
  };
}

// ============================================================================
// List Query Types
// ============================================================================

export interface TransmittalListQuery {
  projectId?: string;
  portalId?: string;
  where?: unknown[];
}

export interface FilterConditions {
  [key: string]: unknown;
}

export interface ViewLayout {
  data: {
    columns?: string[];
    filters?: FilterConditions | unknown[];
  };
}

// ============================================================================
// Form Template Types (uses shared types)
// ============================================================================

/** Form template - uses shared TransmittalFormTemplate type */
export type FormTemplate = TransmittalFormTemplate;

// ============================================================================
// Hook Parameter Types
// ============================================================================

export interface UseCreateTransmittalFormParams {
  projectId: string;
  selectedSpaceId?: string | null;
  selectedReferences?: TransmittalReference[] | null;
  /** Sent as `saveTransmittalsRequest.reviewData.reviewId`, so it carries that field's brand. */
  reviewId?: NonNullable<SaveTransmittalsRequest["reviewData"]>["reviewId"] | null;
  mobile?: boolean;
  api: {
    fetchSpaces: (projectId: string) => Promise<Space[]>;
    fetchFormTemplate: (projectId: string, resourceType: string) => Promise<FormTemplate | null>;
    saveTransmittal: (data: TransmittalPayload) => Promise<{ data: SaveTransmittalsResponse }>;
  };
  callbacks?: {
    onSuccess?: (response: SaveTransmittalsResponse) => void;
    onError?: (error: string) => void;
  };
}

export interface UseTransmittalDetailParams {
  transmittalVersionId: string;
  mobile?: boolean;
  api: {
    fetchTransmittal: (id: string) => Promise<void>;
    /** Called with `TransmittalData["id"]`, i.e. the transmittal's `objectIdString` `_id`. */
    checkAccess: (transmittalId: NonNullable<TransmittalData["id"]>) => Promise<void>;
  };
}

export interface UseTransmittalListParams {
  projectId?: string;
  portalId?: string;
  byPortal?: boolean;
  mobile?: boolean;
}

export interface UseTransmittalPermissionsParams {
  projectSettings?: ProjectSettings | null;
  moduleAccess: ModuleAccess;
  byPortal?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

export const RESOURCE_TYPE_FILE = "RESOURCE_TYPE_FILE";
export const RESOURCE_TYPE_MERGED_FILE = "RESOURCE_TYPE_MERGED_FILE";
export const RESOURCE_TYPE_FOLDER = "RESOURCE_TYPE_FOLDER";
